import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Checkbox,
  Box,
} from '@mui/material';
import type { TableColumn, RowData } from './types';

interface EnhancedTableHeadProps<T = RowData> {
  columns: TableColumn<T>[];
  selectable: boolean;
  selectionMode?: 'single' | 'multiple';
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  onSort?: (field: string) => void;
  hasActions: boolean;
  cellPadding?: string;
  groupColumnHeader?: string;
}

function EnhancedTableHead<T extends object = RowData>(props: EnhancedTableHeadProps<T>) {
  const {
    columns,
    selectable,
    selectionMode = 'multiple',
    allSelected,
    someSelected,
    onSelectAll,
    sortBy,
    sortOrder,
    onSort,
    hasActions,
    cellPadding = '16px 8px',
    groupColumnHeader,
  } = props;

  return (
    <TableHead>
      <TableRow>
        {selectable && selectionMode === 'multiple' && (
          <TableCell padding="checkbox" sx={{ zIndex: 3 }}>
            <Checkbox
              indeterminate={someSelected && !allSelected}
              checked={allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
            />
          </TableCell>
        )}

        {groupColumnHeader && (
          <TableCell
            sx={{
              fontWeight: 600,
              zIndex: 3,
              padding: cellPadding,
              minWidth: 140,
              width: 140,
            }}
          >
            {groupColumnHeader}
          </TableCell>
        )}

        {columns.map((column) => {
          const isSorted = sortBy === column.field;
          const isSortable = column.sortable !== false && onSort;

          return (
            <TableCell
              key={String(column.field)}
              sx={{
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                flex: column.flex,
                fontWeight: 600,
                zIndex: 3,
                padding: cellPadding,
              }}
            >
              {isSortable ? (
                <Box sx={{ display: 'flex', justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'left' ? 'flex-start' : 'center', alignItems: 'center', width: '100%' }}>
                  <TableSortLabel
                    active={isSorted}
                    direction={isSorted && sortOrder === 'DESC' ? 'desc' : 'asc'}
                    onClick={() => onSort(String(column.field))}
                  >
                    {column.headerName}
                  </TableSortLabel>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'left' ? 'flex-start' : 'center', alignItems: 'center', width: '100%' }}>
                  {column.headerName}
                </Box>
              )}
            </TableCell>
          );
        })}

        {hasActions && (
          <TableCell
            sx={{
              fontWeight: 600,
              zIndex: 3,
              padding: cellPadding,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              Actions
            </Box>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
}

export default EnhancedTableHead;
